import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  useListingResource,
  useListingField,
  useFieldUpdater,
  ListingResource as FLCListingResource,
} from '@brightmls/nextgen.lae.flc';
import {
  ListingResource,
  PropertyField,
  RoomField,
} from '../../../system/field';
import { default as ArrowDownIcon } from '@mui/icons-material/KeyboardArrowDown';
import { default as ArrowUpIcon } from '@mui/icons-material/KeyboardArrowUp';
import {
  ResourceField,
  useResourceForm,
} from '../hooks/useResourceForm/useResourceForm';
import { useService } from '../../../contexts/ServiceContext';

export interface Room {
  ROOM_KEY: string;
  ROOM_TYPE: string;
  ROOM_ITEM_NUMBER: number;
  ROOM_LENGTH: number;
  ROOM_WIDTH: number;
  ROOM_LEVEL: string;
  ROOM_DISPLAY_ORDER: number;
  ROOM_FEATURES: string[];
  ROOM_DESCRIPTION?: string | null;
}

interface DragItem {
  key: string;
  index: number;
  itemNumber: number;
}

interface RoomCardProps {
  room: Room;
  index: number;
  onDragStart: (item: DragItem) => void;
  onDragEnd: () => void;
  onDragOver: (index: number) => void;
  onDelete: (room: Room) => void;
  onEditRoom: (rooms: Room) => void;
}

const RoomFields: ResourceField[] = [
  {
    Key: RoomField.ROOM_LEVEL,
    Label: 'Room Level',
    Layout: 'single',
    Metadata: {
      Key: RoomField.ROOM_LEVEL,
      Type: 'Edm.String',
      Lookup: 'Story_Types',
    },
  },
  {
    Key: RoomField.ROOM_TYPE,
    Label: 'Room Type',
    Layout: 'single',
    ForceRequired: true,
    ForceNeverDisabled: true,

    Metadata: {
      Key: RoomField.ROOM_TYPE,
      Type: 'Edm.String',
      Lookup: 'Sub_Room_Types',
    },
  },
  {
    Key: RoomField.ROOM_LENGTH,
    Label: 'Room Length',
    Layout: 'fluid',
    Metadata: {
      Key: RoomField.ROOM_LENGTH,
      Type: 'Edm.Int32',
    },
  },
  {
    Key: RoomField.ROOM_WIDTH,
    Label: 'Room Width',
    Layout: 'fluid',
    Metadata: {
      Key: RoomField.ROOM_WIDTH,
      Type: 'Edm.Int32',
    },
  },
  {
    Key: RoomField.ROOM_FEATURES,
    Label: 'Room Features',
    Layout: 'single',
    Control: 'MultiSelectField',
    Metadata: {
      Key: RoomField.ROOM_FEATURES,
      Type: 'Edm.String',
      Lookup: 'Room_Features',
    },
  },
];

const getNextItemNumber = (existingRooms: Room[]): number => {
  if (!existingRooms.length) return 1;

  const maxItemNumber = Math.max(
    ...existingRooms.map((room) => Number(room.ROOM_ITEM_NUMBER || 0)),
  );

  return maxItemNumber + 1;
};

interface LevelCounts {
  bedrooms: number;
  fullBaths: number;
  halfBaths: number;
  hasRoom: boolean;
}

interface RoomSummary {
  levels: number;
  storyList: string[];
  upperLevels: { [key: number]: LevelCounts };
  lowerLevels: { [key: number]: LevelCounts };
  mainLevel: LevelCounts;
}

const normalizeLevel = (
  level: string,
): { type: 'upper' | 'lower' | 'main'; number?: number } => {
  const normalized = level?.toLowerCase() || '';
  if (normalized === 'unspecified' || normalized === 'main') {
    return { type: 'main' };
  }

  const match = normalized.match(/(upper|lower)\s*(\d+)/);
  if (!match) return { type: 'main' };

  return {
    type: match[1] as 'upper' | 'lower',
    number: parseInt(match[2]),
  };
};

const getRoomType = (roomType: string) => {
  const type = roomType.toLowerCase();
  return {
    isBedroom: type.includes('bedroom'),
    isFullBath:
      (type.includes('bathroom') || type.includes('full bath')) &&
      !type.includes('half bath'),
    isHalfBath: type.includes('half bath'),
  };
};

export const calculateRoomSummary = (rooms: Room[]): RoomSummary => {
  const summary: RoomSummary = {
    levels: 0,
    storyList: ['Main'], // Initialize with Main by default
    upperLevels: {},
    lowerLevels: {},
    mainLevel: { bedrooms: 0, fullBaths: 0, halfBaths: 0, hasRoom: true },
  };

  // Initialize all levels with zero counts
  for (let i = 1; i <= 6; i++) {
    summary.upperLevels[i] = {
      bedrooms: 0,
      fullBaths: 0,
      halfBaths: 0,
      hasRoom: false,
    };
    summary.lowerLevels[i] = {
      bedrooms: 0,
      fullBaths: 0,
      halfBaths: 0,
      hasRoom: false,
    };
  }

  // Track unique levels for story list
  const uniqueUpperLevels = new Set<string>();
  const uniqueLowerLevels = new Set<string>();

  rooms.forEach((room) => {
    const { isBedroom, isFullBath, isHalfBath } = getRoomType(room.ROOM_TYPE);
    const rawLevel = room.ROOM_LEVEL || '';
    const level = normalizeLevel(rawLevel);

    if (rawLevel && rawLevel !== 'Unspecified') {
      if (level.type === 'upper') {
        uniqueUpperLevels.add(rawLevel);
      } else if (level.type === 'lower') {
        uniqueLowerLevels.add(rawLevel);
      }
    }

    // Add to appropriate level counts
    if (level.type === 'main') {
      if (isBedroom) summary.mainLevel.bedrooms++;
      if (isFullBath) summary.mainLevel.fullBaths++;
      if (isHalfBath) summary.mainLevel.halfBaths++;
    } else if (level.number) {
      const counts =
        level.type === 'upper'
          ? summary.upperLevels[level.number]
          : summary.lowerLevels[level.number];

      // there is a room at this level, lets update the room counts
      counts.hasRoom = true;

      if (isBedroom) counts.bedrooms++;
      if (isFullBath) counts.fullBaths++;
      if (isHalfBath) counts.halfBaths++;
    }
  });

  // Calculate total levels (Main level is always counted)
  summary.levels = 1 + uniqueUpperLevels.size + uniqueLowerLevels.size;

  // Create sorted story list with Main always in the middle
  const upperLevels = Array.from(uniqueUpperLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number || 0;
    const numB = normalizeLevel(b).number || 0;
    return numA - numB;
  });

  const lowerLevels = Array.from(uniqueLowerLevels).sort((a, b) => {
    const numA = normalizeLevel(a).number || 0;
    const numB = normalizeLevel(b).number || 0;
    return numA - numB;
  });

  summary.storyList = [...upperLevels, 'Main', ...lowerLevels];

  return summary;
};

export const getRoomSummaryUpdates = (summary: RoomSummary) => {
  const totalBedrooms =
    summary.mainLevel.bedrooms +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    );

  const totalFullBaths =
    summary.mainLevel.fullBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    );

  const totalHalfBaths =
    summary.mainLevel.halfBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    );

  let updates: any = {
    [PropertyField.STORY_LIST]: summary.storyList,

    // Main level
    [PropertyField.MAIN_LEVEL_BEDROOMS]: summary.mainLevel.bedrooms,
    [PropertyField.MAIN_LEVEL_FULL_BATHS]: summary.mainLevel.fullBaths,
    [PropertyField.MAIN_LEVEL_HALF_BATHS]: summary.mainLevel.halfBaths,
  };

  // Add all upper level fields
  for (let i = 1; i <= 6; i++) {
    const upperCounts = summary.upperLevels[i];

    const update =
      upperCounts.hasRoom ||
      upperCounts.bedrooms > 0 ||
      upperCounts.fullBaths > 0 ||
      upperCounts.halfBaths > 0;
    if (!update) {
      updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
      updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
      updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
      continue;
    }

    updates[`UPPER_LEVEL_${i}_BEDROOMS` as PropertyField] =
      upperCounts.bedrooms;
    updates[`UPPER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
      upperCounts.fullBaths;
    updates[`UPPER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
      upperCounts.halfBaths;
  }

  // Add all lower level fields
  for (let i = 1; i <= 6; i++) {
    const lowerCounts = summary.lowerLevels[i];

    const update =
      lowerCounts.hasRoom ||
      lowerCounts.bedrooms > 0 ||
      lowerCounts.fullBaths > 0 ||
      lowerCounts.halfBaths > 0;
    if (!update) {
      updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] = null;
      updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] = null;
      updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] = null;
      continue;
    }

    updates[`LOWER_LEVEL_${i}_BEDROOMS` as PropertyField] =
      lowerCounts.bedrooms;
    updates[`LOWER_LEVEL_${i}_FULL_BATHS` as PropertyField] =
      lowerCounts.fullBaths;
    updates[`LOWER_LEVEL_${i}_HALF_BATHS` as PropertyField] =
      lowerCounts.halfBaths;
  }

  // set totals last
  updates = {
    ...updates,
    [PropertyField.BEDROOMS_TOTAL]: totalBedrooms,
    [PropertyField.BATHROOMS_FULL]: totalFullBaths,
    [PropertyField.BATHROOMS_HALF]: totalHalfBaths,
    [PropertyField.BATHROOMS_TOTAL_INTEGER]: totalFullBaths + totalHalfBaths,
  };

  return updates;
};

const RoomCard = ({
  room,
  index,
  onDragStart,
  onDragEnd,
  onDragOver,
  isDragging,
  isDraggedOver,
  onDelete,
  onEditRoom,
}: RoomCardProps & {
  isDragging: boolean;
  isDraggedOver: boolean;
}) => {
  // Track initial mouse position and whether we're actually dragging
  const [initialMousePos, setInitialMousePos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const dragThresholdRef = useRef(false);

  const dimensions =
    room.ROOM_LENGTH && room.ROOM_WIDTH ? (
      <>
        {room.ROOM_LENGTH} × {room.ROOM_WIDTH}
      </>
    ) : (
      <em>No Dimensions Added</em>
    );

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    // Store initial mouse position
    setInitialMousePos({ x: e.clientX, y: e.clientY });
    dragThresholdRef.current = false;

    // Set a ghost drag image to hide the default one
    const dragGhost = document.createElement('div');
    dragGhost.style.opacity = '0';
    document.body.appendChild(dragGhost);
    e.dataTransfer.setDragImage(dragGhost, 0, 0);
    document.body.removeChild(dragGhost);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    if (
      !initialMousePos ||
      !e.clientX ||
      !e.clientY ||
      dragThresholdRef.current
    )
      return;

    // Calculate distance moved
    const dx = e.clientX - initialMousePos.x;
    const dy = e.clientY - initialMousePos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If moved more than 10px, start the actual drag
    if (distance > 10) {
      dragThresholdRef.current = true;
      e.currentTarget.style.opacity = '0.4';
      onDragStart({
        key: room.ROOM_KEY,
        index,
        itemNumber: room.ROOM_ITEM_NUMBER,
      });
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    const element = e.currentTarget as HTMLDivElement;
    element.style.opacity = '1';
    setInitialMousePos(null);
    dragThresholdRef.current = false;
    onDragEnd();
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    onDragOver(index);
  };

  return (
    <Box
      component="div"
      draggable
      onDragStart={handleDragStart}
      onDrag={handleDrag}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        transform: isDraggedOver ? 'translateY(8px)' : 'none',
        position: 'relative',
        // Smooth transitions for all transformations
        transition: 'all 0.2s ease',
        userSelect: 'none',
        '&:before': isDraggedOver
          ? {
              content: '""',
              position: 'absolute',
              top: -8,
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: '#1f3473',
              borderRadius: '2px',
            }
          : {},
      }}
    >
      {/* Room Header */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
            {room.ROOM_TYPE}
          </Typography>
          <DragIndicatorIcon
            sx={{
              color: 'text.secondary',
              cursor: isDragging ? 'grabbing' : 'grab',
            }}
          />
        </Box>
        {dimensions && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {dimensions}
          </Typography>
        )}
        {room.ROOM_FEATURES && room.ROOM_FEATURES.length > 0 ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {room.ROOM_FEATURES.join(', ')}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <em>No Room Features Added</em>
          </Typography>
        )}
      </Box>

      <Divider />

      <Box
        sx={{
          pl: 1.5,
          pr: 1.5,
          pt: 1,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button
          startIcon={<EditIcon />}
          onClick={() => onEditRoom(room)}
          sx={{
            color: '#1f3473',
            textTransform: 'none',
            '&:hover': { background: 'transparent' },
          }}
        >
          Edit
        </Button>
        <Button
          onClick={() => onDelete(room)}
          startIcon={<DeleteIcon />}
          sx={{
            color: '#D93A3A',
            textTransform: 'none',
            '&:hover': { background: 'transparent' },
          }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  );
};

const LevelAccordion = ({
  level,
  rooms = [],
  onReorder,
  onDeleteRooms,
  onAddRoom,
  onDeleteLevel,
  onEditRoom,
}: {
  level: string;
  rooms: Room[];
  onReorder?: (rooms: Room[]) => void;
  onDeleteRooms: (rooms: Room[]) => void;
  onDeleteLevel: (level: string) => void;
  onEditRoom: (rooms: Room) => void;
  onAddRoom: () => void;
}) => {
  const [isOpen, setIsOpen] = React.useState(true);
  const [draggedItem, setDraggedItem] = React.useState<DragItem | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const levelLabel = level.endsWith('Level') ? level : `${level} Level`;

  const handleDeleteLevel = () => {
    onDeleteLevel(level);
  };

  const handleAddRoom = () => {
    onAddRoom();
  };

  const handleEditRoom = (room: Room) => {
    onEditRoom(room);
  };

  const handleDragStart = (item: DragItem) => {
    setDraggedItem(item);
  };

  const handleDragEnd = () => {
    if (!draggedItem || dragOverIndex === null) {
      setDraggedItem(null);
      setDragOverIndex(null);
      return;
    }

    if (draggedItem.index !== dragOverIndex) {
      const newRooms = [...rooms];
      const draggedRoom = newRooms[draggedItem.index];

      // Remove the dragged item
      newRooms.splice(draggedItem.index, 1);
      // Insert it at the new position
      newRooms.splice(dragOverIndex, 0, draggedRoom);

      // Update display orders with proper spacing for future insertions
      const updatedRooms = newRooms.map((room, index) => ({
        ...room,
        ROOM_DISPLAY_ORDER: index * 1000,
      }));

      // Save the new order
      onReorder?.(updatedRooms);
    }

    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (overIndex: number) => {
    if (!draggedItem) return;
    if (draggedItem.index === overIndex) return;

    setDragOverIndex(overIndex);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ width: '100%', py: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Title and Toggle */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              marginTop: '10px',
              marginBottom: '10px',
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 700 }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {levelLabel} ({rooms.length} Rooms)
            </Typography>
            <IconButton
              onClick={() => setIsOpen(!isOpen)}
              size="small"
              sx={{ p: 0, color: 'black' }}
            >
              {isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
            </IconButton>
          </Box>

          {/* Delete Button */}
          {
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: isMobile ? 1 : 0,
              }}
            >
              <Button
                startIcon={<DeleteIcon />}
                sx={{
                  color: '#D93A3A',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => {
                  handleDeleteLevel();
                }}
              >
                {level === 'Main' ? 'Delete Main Level Rooms' : 'Delete Level'}
              </Button>
            </Box>
          }
        </Box>
      </Box>

      {/* Content */}
      {isOpen && (
        <Box>
          {/* Room List */}
          <Box sx={{ mb: 2 }}>
            {rooms.map((room, index) => (
              <RoomCard
                onEditRoom={() => handleEditRoom(room)}
                key={`${room.ROOM_KEY}-${room.ROOM_ITEM_NUMBER}`}
                room={room}
                index={index}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                isDragging={
                  draggedItem?.key === room.ROOM_KEY &&
                  draggedItem?.itemNumber === room.ROOM_ITEM_NUMBER
                }
                isDraggedOver={dragOverIndex === index}
                onDelete={(room) => onDeleteRooms([room])}
              />
            ))}
            {rooms.length === 0 && (
              <Typography
                color="text.secondary"
                sx={{ textAlign: 'left', py: 2 }}
              >
                You currently do not have any rooms added to this level.
              </Typography>
            )}
          </Box>

          {/* Add Room Button */}
          {
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              onClick={handleAddRoom}
              sx={{
                color: '#1f3473',
                borderColor: '#1f3473',
                textTransform: 'none',
                mt: '10px',
                mb: '5px',
                '&:hover': {
                  borderColor: '#1f3473',
                  backgroundColor: 'rgba(31, 52, 115, 0.04)',
                },
              }}
            >
              Add A Room
            </Button>
          }
        </Box>
      )}
    </Box>
  );
};
const LevelsSummary = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const roomResource = useListingResource<Room>(FLCListingResource.Room);

  // Get summary values
  const rooms = roomResource.items;

  // Calculate summary using existing logic
  const summary = calculateRoomSummary(rooms);

  // Calculate totals using the summary data
  const bedroomsCount =
    summary.mainLevel.bedrooms +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.bedrooms,
      0,
    );

  const bathroomsFullCount =
    summary.mainLevel.fullBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.fullBaths,
      0,
    );

  const bathroomsHalfCount =
    summary.mainLevel.halfBaths +
    Object.values(summary.upperLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    ) +
    Object.values(summary.lowerLevels).reduce(
      (sum, level) => sum + level.halfBaths,
      0,
    );

  const bathroomsTotalCount = bathroomsFullCount + bathroomsHalfCount;

  // Get levels count from story list
  const levelsCount = summary.storyList.length;

  return (
    <Box
      sx={{
        mb: 4,
        bgcolor: '#f5f5f5',
        p: 3,
        borderRadius: 1,
        marginLeft: '-20px',
        marginRight: '-20px',
      }}
    >
      <Typography sx={{ fontSize: '18px', fontWeight: 600 }} gutterBottom>
        Levels & Rooms Summary
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          position: 'relative',
        }}
      >
        {/* Left Column */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Bedrooms Count</Typography>
            <Typography>{bedroomsCount}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Levels Count</Typography>
            <Typography>{levelsCount}</Typography>
          </Box>
        </Box>

        {/* Divider */}
        <Divider
          orientation={isMobile ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            mx: { sm: 3 },
            my: { xs: 1, sm: 0 },
            borderColor: 'rgba(0, 0, 0, 0.12)',
          }}
        />

        {/* Right Column */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Bathrooms Half</Typography>
            <Typography>{bathroomsHalfCount}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Bathrooms Full</Typography>
            <Typography>{bathroomsFullCount}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Bathrooms Total Count</Typography>
            <Typography>{bathroomsTotalCount}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const LevelsAndRooms = () => {
  const roomResource = useListingResource<Room>(FLCListingResource.Room);
  const updateFields = useFieldUpdater(FLCListingResource.Property);

  const countyField = useListingField(
    FLCListingResource.Property,
    PropertyField.COUNTY,
    '',
  );
  const listingKeyField = useListingField(
    FLCListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const editKeyField = useListingField(
    FLCListingResource.Property,
    PropertyField.EDIT_KEY,
    null,
  );

  const [roomsState, setRoomsState] = useState<Room[]>([]);
  const { openResourceForm } = useResourceForm();
  const { deleteListingResources } = useService();
  const latestRoomsRef = useRef<Room[]>([]);

  const county = countyField.value;
  const listingKey = listingKeyField.value;
  const editKey = editKeyField.value;

  useEffect(() => {
    const rooms = roomResource.items;
    latestRoomsRef.current = rooms;
    setRoomsState(rooms);
  }, [roomResource.items]);

  const handleDeleteLevel = async (level: string) => {
    // Use the ref value to ensure we have the latest state
    const rooms = latestRoomsRef.current;
    const roomsToDelete = rooms.filter((room) => {
      // Normalize level names for comparison
      const roomLevel =
        room.ROOM_LEVEL === 'Unspecified' ? 'Main' : room.ROOM_LEVEL;
      const targetLevel = level === 'Main Level' ? 'Main' : level;
      return roomLevel === targetLevel;
    });

    handleDeleteRooms(roomsToDelete);
  };

  const handleDeleteRooms = async (roomsToDelete: Room[]) => {
    // Use ref for latest state
    const currentRooms = latestRoomsRef.current;
    const updatedRooms = currentRooms.filter((room) => {
      return !roomsToDelete.some(
        (deleteRoom) => deleteRoom.ROOM_ITEM_NUMBER === room.ROOM_ITEM_NUMBER,
      );
    });

    // Log what we're deleting to help debug
    console.log(
      'Deleting rooms:',
      roomsToDelete.map((r) => ({
        level: r.ROOM_LEVEL,
        itemNumber: r.ROOM_ITEM_NUMBER,
      })),
      currentRooms,
      updatedRooms,
    );

    // Update all state sources atomically
    latestRoomsRef.current = updatedRooms;
    setRoomsState(updatedRooms);
    roomResource.set(updatedRooms);

    // Update summary with latest state
    const summary = calculateRoomSummary(updatedRooms);
    const updates = getRoomSummaryUpdates(summary);

    // Batch update all summary fields
    updateFields(updates);

    // Delete from backend
    await deleteListingResources(
      Number(listingKey),
      roomsToDelete.map((room) => ({
        type: 'Room' as const,
        itemNumber: Number(room.ROOM_ITEM_NUMBER),
      })),
    ).catch((error) => {
      console.warn('Error deleting rooms:', error);
    });
  };

  const saveHook = (room: any, isCreate: boolean) => {
    room.COUNTY = county;
    room.ROOM_LISTING_KEY = listingKey;
    room.ROOM_EXISTS_YN = true;
    room.ROOM_SYSTEM_LOCALE = 'BRIGHT';
    room.ROOM_SUB_SYSTEM_LOCALE = 'BRIGHT_MAIN';
    room.ROOM_CHANGE_TIMESTAMP = toSystemFormat(Date.now(), true);
    room.ROOM_CHECK_OUT_TIMESTAMP =
      room.ROOM_CHECK_OUT_TIMESTAMP || toSystemFormat(Date.now(), true);
    room.ROOMLIS_KEY = room.ROOMLIS_KEY || editKey;
    room.ROOM_ITEM_NUMBER = room.ROOM_ITEM_NUMBER || roomsState.length + 1;
    if (isCreate) {
      const allRooms = [...latestRoomsRef.current];
      room.ROOM_ITEM_NUMBER = getNextItemNumber(allRooms);

      room.ROOM_DISPLAY_ORDER = roomsState.length * 1000;
    }

    const updatedRoom = { ...room };

    // Get current rooms from resource
    const currentRooms = [...latestRoomsRef.current];
    let newRoomsState;

    if (isCreate) {
      newRoomsState = [...currentRooms, updatedRoom];
    } else {
      newRoomsState = currentRooms.map((r) =>
        r.ROOM_ITEM_NUMBER === updatedRoom.ROOM_ITEM_NUMBER ? updatedRoom : r,
      );
    }

    // Update both our ref and state
    latestRoomsRef.current = newRoomsState;
    setRoomsState(newRoomsState);
    roomResource.set(newRoomsState);

    // Calculate new summary
    const summary = calculateRoomSummary(newRoomsState);
    const updates = getRoomSummaryUpdates(summary);

    // Update all the summary fields in the listing context
    updateFields(updates);

    return room;
  };

  const handleAddRoom = (defaultLevel: string = 'Main') => {
    openResourceForm(
      {
        fields: RoomFields,
        resourceType: ListingResource.Room,
        saveAndAddAnother: true,
        internalResourceManagement: true,
      },
      {
        title: 'Add Room',
      },
      saveHook,
      {
        ROOM_LEVEL: defaultLevel,
      },
    );
  };

  const handleEditRoom = (room: Room) => {
    const allRooms = roomResource.items;
    const roomIndex = allRooms.findIndex(
      (allRoomItem) => allRoomItem.ROOM_ITEM_NUMBER === room.ROOM_ITEM_NUMBER,
    );

    openResourceForm(
      {
        fields: RoomFields,
        resourceType: ListingResource.Room,
        resourceIndex: roomIndex,
        internalResourceManagement: true,
      },
      {
        title: 'Edit Room',
      },
      saveHook,
      {
        ...room,
      },
    );
  };

  // Get rooms and organize by level with proper sorting
  const roomsByLevel = useMemo(() => {
    const allRooms = roomsState.length > 0 ? roomsState : roomResource.items;

    // First group by level, ensuring Main level exists
    const grouped: Record<string, Room[]> = {
      Main: [], // Always initialize Main level
    };

    allRooms.forEach((room) => {
      const level =
        room.ROOM_LEVEL === 'Unspecified' ? 'Main' : room.ROOM_LEVEL;
      if (!grouped[level]) {
        grouped[level] = [];
      }
      grouped[level].push(room);
    });

    // Then sort each level's rooms by display order
    Object.keys(grouped).forEach((level) => {
      grouped[level].sort((a: Room, b: Room) => {
        const orderA = a.ROOM_DISPLAY_ORDER || 0;
        const orderB = b.ROOM_DISPLAY_ORDER || 0;
        return orderA - orderB;
      });
    });

    return grouped;
  }, [roomResource.items, roomsState]);

  const handleReorder = (level: string, reorderedRooms: Room[]) => {
    // Update display orders with proper spacing for future insertions
    const updatedRooms = reorderedRooms.map((room, index) => ({
      ...room,
      ROOM_DISPLAY_ORDER: index * 1000,
    }));

    // Update all rooms, maintaining rooms from other levels
    const newRoomsState = roomsState.map((room) => {
      if (
        room.ROOM_LEVEL === level ||
        (level === 'Main' && room.ROOM_LEVEL === 'Unspecified')
      ) {
        const updatedRoom = updatedRooms.find(
          (r) => r.ROOM_ITEM_NUMBER === room.ROOM_ITEM_NUMBER,
        );
        return updatedRoom || room;
      }
      return room;
    });

    setRoomsState(newRoomsState);

    // Update display orders in listing context
    updatedRooms.forEach((room) => {
      const roomIndex = roomResource.findIndex(
        (r: Room) => r.ROOM_ITEM_NUMBER === room.ROOM_ITEM_NUMBER,
      );
      if (roomIndex !== -1) {
        roomResource.update(roomIndex, room);
      }
    });
  };

  // Initial load
  useEffect(() => {
    if (!roomsState.length) {
      const initialRooms = roomResource.items;
      setRoomsState(initialRooms);
    }
  }, [roomResource.items]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: '-15px',
        }}
      >
        <Typography variant="body2" color="text.primary" sx={{ mb: 3 }}>
          You are required to enter the number of Bedrooms and Full Baths on the
          level they are located.
        </Typography>
      </Box>
      <LevelsSummary />
      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={() => handleAddRoom()}
        sx={{
          color: '#1f3473',
          borderColor: '#1f3473',
          textTransform: 'none',
          mb: 2,
          '&:hover': {
            borderColor: '#1f3473',
            backgroundColor: 'rgba(31, 52, 115, 0.04)',
          },
        }}
      >
        Add A Room
      </Button>
      {/* Show Upper Level if it exists */}
      {Object.entries(roomsByLevel)
        .sort(([levelA], [levelB]) => {
          const parseLevel = (level: string) => {
            if (level === 'Main') return { type: 'main', num: 0 };
            const match = level.match(/(Upper|Lower)\s*(\d+)/i);
            if (!match) return { type: '', num: 0 };
            return {
              type: match[1].toLowerCase(),
              num: parseInt(match[2]),
            };
          };

          const a = parseLevel(levelA);
          const b = parseLevel(levelB);

          // Different types: Upper -> Main -> Lower
          if (a.type !== b.type) {
            if (a.type === 'upper') return -1;
            if (b.type === 'upper') return 1;
            if (a.type === 'main') return -1;
            if (b.type === 'main') return 1;
            return a.type === 'lower' ? -1 : 1;
          }

          // Same type: Upper in reverse order, Lower in ascending order
          if (a.type === 'upper') {
            return b.num - a.num; // Reverse order for upper
          }
          return a.num - b.num; // Normal order for lower
        })
        .map(([level, rooms]) => (
          <LevelAccordion
            key={`${level}`}
            onDeleteLevel={handleDeleteLevel}
            level={level}
            onDeleteRooms={handleDeleteRooms}
            onEditRoom={(room) => handleEditRoom(room)}
            rooms={rooms}
            onAddRoom={() => handleAddRoom(level)}
            onReorder={(rooms) => handleReorder(level, rooms)}
          />
        ))}
    </Box>
  );
};

export default LevelsAndRooms;
